import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShotVisualizationSystem } from '@/features/shot-visualization';
import { LiveShotMonitor } from '@/features/shot-visualization/components/LiveShotMonitor';
import { ShotAnalyticsView } from '@/features/shot-visualization/components/ShotAnalyticsView';
import { MultiShotComparison } from '@/features/shot-visualization/components/MultiShotComparison';
import { ReportExport } from '@/features/shot-visualization/components/ReportExport';
import { PerfectShotOptimizerUI } from '@/features/shot-visualization/components/PerfectShotOptimizer';
import { VideoAnalysis } from '@/features/shot-visualization/components/VideoAnalysis';
import { useSessionShots, useRecentShots } from '@/features/shot-visualization/hooks/useShots';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Activity, BarChart3, GitCompare, Target, FileText, Brain, Video } from 'lucide-react';
import { Link } from 'react-router-dom';

export const ShotVisualizationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId') || undefined;
  const [activeTab, setActiveTab] = useState('visualization');
  const { user } = useAuth();
  
  // Refs for chart elements (for PDF export)
  const analyticsRef = useRef<HTMLDivElement>(null);
  const comparisonRef = useRef<HTMLDivElement>(null);
  
  // Fetch shots data
  const { data: sessionShots = [] } = useSessionShots(sessionId);
  const { data: recentShots = [] } = useRecentShots(20);
  
  const shots = sessionId ? sessionShots : recentShots;
  
  // Chart elements for export
  const chartElements = {
    analytics: analyticsRef.current,
    comparison: comparisonRef.current
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Dashboard
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Target className="h-6 w-6" />
                  Shot Visualization & Analytics
                </h1>
                <p className="text-muted-foreground">
                  Real-time 3D visualization and comprehensive shot analysis
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {sessionId && (
                <Badge variant="outline" className="gap-1">
                  Session: {sessionId}
                </Badge>
              )}
              <Badge variant="outline" className="gap-1">
                <Activity className="h-3 w-3" />
                {shots.length} shots
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="visualization" className="gap-2">
              <Target className="h-4 w-4" />
              3D View
            </TabsTrigger>
            <TabsTrigger value="live" className="gap-2">
              <Activity className="h-4 w-4" />
              Live
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="comparison" className="gap-2">
              <GitCompare className="h-4 w-4" />
              Compare
            </TabsTrigger>
            <TabsTrigger value="optimizer" className="gap-2">
              <Brain className="h-4 w-4" />
              Optimizer
            </TabsTrigger>
            <TabsTrigger value="video" className="gap-2">
              <Video className="h-4 w-4" />
              Video
            </TabsTrigger>
            <TabsTrigger value="export" className="gap-2">
              <FileText className="h-4 w-4" />
              Export
            </TabsTrigger>
          </TabsList>

          <TabsContent value="visualization" className="space-y-4">
            <ShotVisualizationSystem sessionId={sessionId} />
          </TabsContent>

          <TabsContent value="live" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <ShotVisualizationSystem sessionId={sessionId} />
              </div>
              <div className="lg:col-span-1">
                <LiveShotMonitor 
                  sessionId={sessionId}
                  onShotSelect={(shot) => {
                    // Load selected shot in visualization
                    console.log('Selected shot:', shot);
                  }}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-4">
            <div ref={analyticsRef}>
              <ShotAnalyticsView 
                shots={shots}
                timeRange="all"
                onTimeRangeChange={(range) => console.log('Time range:', range)}
              />
            </div>
          </TabsContent>

          <TabsContent value="comparison" className="space-y-4">
            <div ref={comparisonRef}>
              <MultiShotComparison 
                shots={shots}
                maxComparisons={5}
                onExport={(data) => {
                  console.log('Export comparison data:', data);
                  // Implement export functionality
                }}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="optimizer" className="space-y-4">
            <PerfectShotOptimizerUI
              currentShot={shots[0]}
              historicalShots={shots}
              playerProfile={{
                id: user?.id || '1',
                name: user?.name || 'Player',
                height: 175,
                weight: 75,
                armLength: 70,
                battingStyle: 'right',
                experienceLevel: 'intermediate',
                strengths: [],
                weaknesses: [],
                averageBatSpeed: 30,
                averagePower: 0.7,
                preferredShotTypes: ['drive', 'cut']
              }}
            />
          </TabsContent>
          
          <TabsContent value="video" className="space-y-4">
            <VideoAnalysis
              onAnalysisComplete={(result) => {
                console.log('Video analysis complete:', result);
              }}
            />
          </TabsContent>
          
          <TabsContent value="export" className="space-y-4">
            <ReportExport
              shots={shots}
              sessionId={sessionId}
              playerName={user?.name || 'Player'}
              chartElements={chartElements}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};